import '../account/account.css';
import { useRef, useState } from 'react';
import {
  fieldId,
  SelectField,
  SuggestField,
  TextField,
  useForm,
  type FormErrors,
  type FormValues,
} from '../../auth/CompanyFields';
import { t, tl } from '../../i18n';
import { today } from '../../lib/format';
import { errorsOf, inPart, rules, validate, type Part, type UploadKind } from './checks';
import { KRANKENKASSEN } from './krankenkassen';

// Personalfragebogen wie in der Demo, mit Ausweis. Füllt der Mandant alles aus („alles“), oder mit Link: Der Mandant
// trägt Name und Beschäftigung ein („firma“), der Mitarbeiter auf seiner Seite den Rest („mitarbeiter“).

// Auswahlwerte gehen so an den Server (Deutsch); SelectField zeigt sie übersetzt an
const ART = [tl('Vollzeit'), tl('Teilzeit'), tl('Minijob'), tl('Werkstudent'), tl('Auszubildende/r')];
const GESCHLECHT = [tl('weiblich'), tl('männlich'), tl('divers')];
const FAMILIENSTAND = [tl('ledig'), tl('verheiratet'), tl('eingetragene Lebenspartnerschaft'), tl('geschieden'), tl('verwitwet')];
const WEITERE = [tl('nein'), tl('ja, Hauptbeschäftigung'), tl('ja, Minijob')];
const RV = [tl('ja, Antrag auf Befreiung liegt vor'), tl('nein, zahlt Rentenbeitrag mit')];
const SCHULE = [tl('ohne Abschluss'), tl('Haupt-/Volksschulabschluss'), tl('Mittlere Reife'), tl('Abitur/Fachabitur')];
const BERUF = [
  tl('keine'),
  tl('anerkannte Berufsausbildung'),
  tl('Meister/Techniker'),
  tl('Bachelor'),
  tl('Diplom/Master'),
  tl('Promotion'),
];
const KONFESSION = [tl('keine'), tl('evangelisch'), tl('römisch-katholisch'), tl('andere')];
const AUSWEIS = [tl('Personalausweis'), tl('Reisepass')];
const JA_NEIN = [tl('nein'), tl('ja')];

const UPLOADS: { key: UploadKind; label: string }[] = [
  { key: 'ausweis_vorne', label: tl('Ausweis Vorderseite') },
  { key: 'ausweis_hinten', label: tl('Ausweis Rückseite') },
  { key: 'aufenthaltstitel', label: tl('Aufenthaltstitel / Arbeitserlaubnis (nur außerhalb der EU)') },
  { key: 'immatrikulation', label: tl('Immatrikulationsbescheinigung') },
];

// Reihenfolge im Formular (für den Fokus auf den ersten Fehler)
const ORDER = [
  'vorname',
  'nachname',
  'geburtsname',
  'geschlecht',
  'geburtsdatum',
  'geburtsort',
  'geburtsland',
  'staatsangehoerigkeit',
  'strasse',
  'plz',
  'ort',
  'email',
  'ausweisart',
  ...UPLOADS.map((u) => u.key),
  'eintritt',
  'befristetBis',
  'taetigkeit',
  'art',
  'stunden',
  'tage',
  'urlaub',
  'brutto',
  'stundenlohn',
  'weitere',
  'rvBefreiung',
  'steuerId',
  'steuerklasse',
  'kinderfreibetraege',
  'svNummer',
  'krankenkasse',
  'kinder',
  'iban',
];

const iso = (d: { year: number; month: number; day: number }) =>
  `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

export type Questionnaire = ReturnType<typeof useQuestionnaire>;

// Dateien der alten Personalakte bei einer Wiedereinstellung
export interface OldFiles {
  svKeine: boolean;
  files: UploadKind[];
  ausweisart: string | null;
}

// Zustand des Fragebogens. initial: z. B. Name und Beschäftigungsart aus dem Link (Steuerklasse und Immatrikulation
// hängen von der Art ab). old: Wiedereinstellung, die Dateien der alten Akte gelten, solange nichts Neues kommt
// (Ausweis nur bei derselben Ausweisart, Immatrikulation nur für Werkstudenten; wie der Server).
export function useQuestionnaire(part: Part, initial: FormValues = {}, old?: OldFiles) {
  const f = useForm(initial);
  const [svKeine, setSvKeine] = useState(old?.svKeine ?? false);
  // Einverständnis zur Ausweiskopie (Sam 12.10.): ohne Häkchen keine Ausweis-Fotos, der Ausweis wird persönlich gezeigt
  const [ausweisKopie, setAusweisKopie] = useState(false);
  const [files, setFiles] = useState<Partial<Record<UploadKind, File>>>({});
  const now = today();
  const todayIso = iso(now);
  const r = rules(f.values, svKeine, ausweisKopie);
  const kept = new Set(
    (old?.files ?? []).filter(
      (k) =>
        (!k.startsWith('ausweis') || f.values.ausweisart === old!.ausweisart) &&
        (k !== 'immatrikulation' || f.values.art === 'Werkstudent'),
    ),
  );
  const fileLabel = (k: UploadKind) =>
    k === 'ausweis_vorne' && r.pass
      ? t('Reisepass (Seite mit Foto)')
      : t((UPLOADS.find((u) => u.key === k)?.label ?? k).replace(/ \(.*\)$/, ''));
  const shown = (k: UploadKind) =>
    (!k.startsWith('ausweis') || ausweisKopie) && (k !== 'ausweis_hinten' || !r.pass) && (k !== 'immatrikulation' || r.ws);

  const setConsent = (ok: boolean) => {
    setAusweisKopie(ok);
    if (f.errors.ausweis_vorne || f.errors.ausweis_hinten) {
      f.setErrors({ ...f.errors, ausweis_vorne: undefined, ausweis_hinten: undefined });
    }
  };

  const setFile = (k: UploadKind, file: File | undefined) => {
    setFiles((x) => ({ ...x, [k]: file }));
    if (f.errors[k]) f.setErrors({ ...f.errors, [k]: undefined });
  };

  // Prüft den eigenen Teil. Bei einem Fehler: Meldungen an die Felder, Fokus auf das erste; gibt dessen Meldung zurück.
  const check = (): string | null => {
    const have = { ...Object.fromEntries([...kept].map((k) => [k, true])), ...files };
    const errors: FormErrors = errorsOf(validate(f.values, svKeine, have, todayIso, fileLabel, ausweisKopie), part);
    const first = ORDER.find((k) => errors[k]);
    if (!first) return null;
    f.setErrors(errors);
    const el = document.getElementById(fieldId(first));
    el?.focus();
    el?.scrollIntoView({ block: 'center' });
    return errors[first]!;
  };

  // Angaben, wie der Server sie erwartet (leere Felder als null)
  const data = () => {
    const v = f.values;
    const s = (k: string) => (v[k] ?? '').trim() || null;
    const n = (k: string) => (s(k) ? Number(s(k)!.replace(',', '.')) : null);
    return {
      vorname: s('vorname'),
      nachname: s('nachname'),
      geburtsname: s('geburtsname'),
      geschlecht: s('geschlecht'),
      geburtsdatum: s('geburtsdatum'),
      geburtsort: s('geburtsort'),
      geburtsland: s('geburtsland'),
      staatsangehoerigkeit: s('staatsangehoerigkeit'),
      familienstand: s('familienstand'),
      telefon: s('telefon'),
      strasse: s('strasse'),
      plz: s('plz'),
      ort: s('ort'),
      email: s('email'),
      ausweisart: s('ausweisart'),
      eintritt: s('eintritt'),
      befristetBis: s('befristetBis'),
      taetigkeit: s('taetigkeit'),
      art: s('art'),
      stunden: n('stunden'),
      tage: n('tage'),
      urlaub: n('urlaub'),
      brutto: n('brutto'),
      stundenlohn: n('stundenlohn'),
      weitere: r.mini ? s('weitere') : null,
      rvBefreiung: r.mini ? s('rvBefreiung') : null,
      schulabschluss: s('schulabschluss'),
      berufsausbildung: s('berufsausbildung'),
      steuerId: s('steuerId'),
      steuerklasse: s('steuerklasse'),
      kinderfreibetraege: n('kinderfreibetraege'),
      konfession: s('konfession'),
      svNummer: svKeine ? null : s('svNummer'),
      svKeine,
      krankenkasse: s('krankenkasse'),
      kinder: n('kinder'),
      schwerbehinderung: s('schwerbehinderung'),
      iban: s('iban'),
      kontoinhaber: s('kontoinhaber'),
      // den Ausweis-Teil füllt beim Link der Mitarbeiter aus
      ...(part === 'firma' ? {} : { ausweisKopie }),
    };
  };

  // Mit Dateien: zuerst die Angaben, dann die Dateien (so prüft der Server die Angaben, bevor er Dateien speichert)
  const formData = () => {
    const fd = new FormData();
    fd.append('data', JSON.stringify(data()));
    for (const u of UPLOADS) {
      const file = files[u.key];
      if (file && shown(u.key)) fd.append(u.key, file, file.name);
    }
    return fd;
  };

  return {
    part,
    f,
    svKeine,
    setSvKeine,
    ausweisKopie,
    setAusweisKopie: setConsent,
    files,
    kept,
    setFile,
    r,
    now,
    todayIso,
    shown,
    check,
    data,
    formData,
  };
}

// Die Abschnitte des Fragebogens, die zu diesem Teil gehören
export function QuestionnaireFields({ q, linkHint }: { q: Questionnaire; linkHint?: boolean }) {
  const { f, r, now, todayIso, svKeine, part } = q;
  const v = f.values;
  const has = (k: string) => inPart(k, part);
  const req = (k: string) => r.required.has(k);
  const g = (k: string, label: string, props: Record<string, unknown> = {}) => (
    <TextField f={f} name={k} label={label} required={req(k)} autoComplete="off" {...props} />
  );
  const sel = (k: string, label: string, options: string[]) => (
    <SelectField f={f} name={k} label={label} required={req(k)} options={options} placeholder={t('Bitte wählen')} />
  );
  const num = { type: 'number', min: 0, step: 'any', inputMode: 'decimal' };

  // Sofortmeldung: Eintritt heute, morgen oder schon vorbei. Branchen wie in server/src/modules/lohn/branche.ts (§ 2a
  // SchwarzArbG), hier gekürzt
  const tomorrow = new Date(Date.UTC(now.year, now.month - 1, now.day + 1)).toISOString().slice(0, 10);
  const ein = v.eintritt ?? '';
  const late = !!ein && ein < todayIso;
  const soon = !!ein && !late && ein <= tomorrow;
  const einHint =
    (late
      ? t('⚠ Der Eintritt war schon. Bitte gleich senden, die Anmeldung ist fällig.')
      : soon
        ? ein === todayIso
          ? t(
              '⚠ Eintritt heute: bitte sofort senden. In Bau, Reinigung, Sicherheit, Gastronomie, Transport, Friseur und einigen weiteren Branchen muss die Meldung vor Arbeitsbeginn raus.',
            )
          : t(
              '⚠ Eintritt morgen: bitte sofort senden. In Bau, Reinigung, Sicherheit, Gastronomie, Transport, Friseur und einigen weiteren Branchen muss die Meldung vor Arbeitsbeginn raus.',
            )
        : t(
            'Bau, Reinigung, Sicherheit, Gastronomie, Transport, Friseur und einige weitere Branchen: bitte vor dem ersten Arbeitstag melden.',
          )) + (linkHint && (late || soon) ? ` ${t('Wenn es so eilt, füllen Sie besser alles selbst aus.')}` : '');

  return (
    <>
      {part === 'firma' ? (
        <fieldset>
          <legend>{t('Mitarbeiter')}</legend>
          <div className="two">
            {g('vorname', t('Vorname'))}
            {g('nachname', t('Nachname'))}
          </div>
        </fieldset>
      ) : (
        <fieldset>
          <legend>{t('Person')}</legend>
          <div className="two">
            {g('vorname', t('Vorname'))}
            {g('nachname', t('Nachname'))}
          </div>
          <div className="two">
            {g('geburtsname', t('Geburtsname (falls abweichend)'))}
            {sel('geschlecht', t('Geschlecht'), GESCHLECHT)}
          </div>
          <div className="two">
            {g('geburtsdatum', t('Geburtsdatum'), {
              type: 'date',
              min: `${now.year - 100}${todayIso.slice(4)}`,
              max: `${now.year - 13}${todayIso.slice(4)}`,
            })}
            {g('geburtsort', t('Geburtsort'))}
          </div>
          <div className="two">
            {g('geburtsland', t('Geburtsland'))}
            {g('staatsangehoerigkeit', t('Staatsangehörigkeit'))}
          </div>
          <div className="two">
            {sel('familienstand', t('Familienstand'), FAMILIENSTAND)}
            {g('telefon', t('Telefon'), { type: 'tel' })}
          </div>
          {g('strasse', t('Straße und Hausnummer'))}
          <div className="two">
            {g('plz', t('PLZ'), { inputMode: 'numeric', maxLength: 5 })}
            {g('ort', t('Ort'))}
          </div>
          {g('email', t('E-Mail'), { type: 'email' })}
        </fieldset>
      )}

      {has('ausweisart') && (
        <fieldset>
          <legend>{t('Ausweis')}</legend>
          {sel('ausweisart', t('Ausweisart'), AUSWEIS)}
          <label className="chk" htmlFor="ma-ausweiskopie">
            <input
              type="checkbox"
              id="ma-ausweiskopie"
              checked={q.ausweisKopie}
              onChange={(e) => q.setAusweisKopie(e.target.checked)}
            />
            <span>
              {part === 'mitarbeiter'
                ? t(
                    'Ich bin einverstanden, dass eine Kopie meines Ausweises gespeichert wird, damit meine Angaben geprüft werden können. Ich kann das jederzeit widerrufen, dann wird die Kopie gelöscht.',
                  )
                : t(
                    'Der Mitarbeiter ist einverstanden, dass eine Kopie seines Ausweises gespeichert wird, damit seine Angaben geprüft werden können. Er kann das jederzeit widerrufen, dann wird die Kopie gelöscht.',
                  )}
            </span>
          </label>
          {!q.ausweisKopie && (
            <p className="fh" data-testid="ausweis-persoenlich">
              {part === 'mitarbeiter'
                ? t('Ohne Häkchen wird keine Kopie gespeichert. Bitte zeigen Sie Ihren Ausweis Ihrem Arbeitgeber persönlich.')
                : t('Ohne Einverständnis wird keine Kopie gespeichert. Bitte lassen Sie sich den Ausweis persönlich zeigen.')}
            </p>
          )}
          {UPLOADS.filter((u) => q.shown(u.key)).map((u) => (
            <Upload
              key={u.key}
              k={u.key}
              label={u.key === 'ausweis_vorne' && r.pass ? t('Reisepass (Seite mit Foto)') : t(u.label)}
              required={r.files[u.key]}
              file={q.files[u.key]}
              kept={q.kept.has(u.key)}
              error={f.errors[u.key]}
              onFile={(file) => q.setFile(u.key, file)}
            />
          ))}
        </fieldset>
      )}

      {has('eintritt') && (
        <fieldset>
          <legend>{t('Beschäftigung')}</legend>
          <div className="two">
            <div className="fldw">
              {g('eintritt', t('Eintritt'), { type: 'date', min: '1950-01-01', max: '2100-12-31' })}
              <small className={`fh${late || soon ? ' warn' : ''}`}>{einHint}</small>
            </div>
            {g('befristetBis', t('Befristet bis (leer = unbefristet)'), {
              type: 'date',
              min: ein || '1950-01-01',
              max: '2100-12-31',
            })}
          </div>
          {g('taetigkeit', t('Tätigkeit'))}
          <div className="two">
            {sel('art', t('Beschäftigungsart'), ART)}
            {g('stunden', t('Wöchentliche Arbeitsstunden'), num)}
          </div>
          <div className="two">
            {g('tage', t('Arbeitstage pro Woche'), num)}
            {g('urlaub', t('Urlaubstage pro Jahr'), num)}
          </div>
          <div className="two">
            {g('brutto', t('Brutto pro Monat (€)'), num)}
            {g('stundenlohn', t('Stundenlohn (€, statt Monatsbrutto)'), num)}
          </div>
          {sel('weitere', t('Weitere Beschäftigung bei einem anderen Arbeitgeber?'), WEITERE)}
          {r.mini && sel('rvBefreiung', t('Befreiung von der Rentenversicherung (Minijob)'), RV)}
        </fieldset>
      )}

      {has('steuerId') && (
        <>
          <fieldset>
            <legend>{t('Ausbildung')}</legend>
            {sel('schulabschluss', t('Höchster Schulabschluss'), SCHULE)}
            {sel('berufsausbildung', t('Höchste Berufsausbildung'), BERUF)}
          </fieldset>

          <fieldset>
            <legend>{t('Steuer')}</legend>
            <div className="two">
              {g('steuerId', t('Steuer-ID (11 Ziffern)'), { inputMode: 'numeric' })}
              {sel('steuerklasse', t('Steuerklasse'), ['1', '2', '3', '4', '5', '6'])}
            </div>
            <div className="two">
              {g('kinderfreibetraege', t('Kinderfreibeträge'), num)}
              {sel('konfession', t('Konfession'), KONFESSION)}
            </div>
          </fieldset>

          <fieldset>
            <legend>{t('Sozialversicherung')}</legend>
            {g('svNummer', t('Sozialversicherungsnummer'), {
              disabled: svKeine,
              autoCapitalize: 'characters',
              spellCheck: false,
            })}
            <label className="chk" htmlFor="ma-nosv">
              <input
                type="checkbox"
                id="ma-nosv"
                checked={svKeine}
                onChange={(e) => {
                  q.setSvKeine(e.target.checked);
                  if (e.target.checked) f.set('svNummer', '');
                }}
              />
              <span>{t('Noch keine Sozialversicherungsnummer (z. B. erste Beschäftigung oder Zuzug aus dem Ausland)')}</span>
            </label>
            <div className="two">
              <SuggestField
                f={f}
                name="krankenkasse"
                label={t('Krankenkasse')}
                required={req('krankenkasse')}
                options={KRANKENKASSEN}
                placeholder={t('z. B. AOK, TK, DAK')}
                maxLength={160}
              />
              {g('kinder', t('Anzahl Kinder (für die Pflegeversicherung)'), num)}
            </div>
            {sel('schwerbehinderung', t('Schwerbehinderung'), JA_NEIN)}
          </fieldset>

          <fieldset>
            <legend>{t('Bankverbindung')}</legend>
            {g('iban', 'IBAN', { autoCapitalize: 'characters', spellCheck: false })}
            {g('kontoinhaber', t('Kontoinhaber (falls abweichend)'))}
          </fieldset>
        </>
      )}
    </>
  );
}

// Datei zum Personalfragebogen: Foto machen oder Datei wählen (am Handy bietet das System beides an)
export function Upload({
  k,
  label,
  required,
  file,
  kept,
  error,
  onFile,
}: {
  k: string;
  label: string;
  required: boolean;
  file: File | undefined;
  // schon da (aus der alten Personalakte), ein neues Foto ersetzt es
  kept?: boolean;
  error: string | undefined;
  onFile(file: File | undefined): void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const id = fieldId(k);
  return (
    <div className="fld">
      <button
        type="button"
        id={id}
        className={`upl${file || kept ? ' done' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onClick={() => ref.current?.click()}
      >
        <span className="ico2" aria-hidden="true">
          ◉
        </span>
        <span>
          <span className="ut">{label}</span>
          {required && (
            <>
              {' '}
              <span className="req" aria-hidden="true">
                *
              </span>
            </>
          )}
          <small>
            {file
              ? `✓ ${file.name}`
              : kept
                ? t('✓ aus der alten Personalakte · tippen für ein neues Foto')
                : t('Foto machen oder Datei wählen')}
          </small>
        </span>
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        hidden
        data-testid={`ma-${k}`}
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {error && (
        <p className="ferr" id={`${id}-err`}>
          {error}
        </p>
      )}
    </div>
  );
}
